import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getTermsSections, type TermsSection } from '@/lib/terms-data';
import { scrollToElement } from '@/utils/scrollUtils';

const DPANav: React.FC = () => {
  const [sections, setSections] = useState<TermsSection[]>([]);
  const [activeSection, setActiveSection] = useState<string>('');
  const [isManualScroll, setIsManualScroll] = useState(false);
  const [intersectionRatios, setIntersectionRatios] = useState<{ [key: string]: number }>({});
  const [hasUserScrolled, setHasUserScrolled] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();
  const navHeight = 96;

  useEffect(() => {
    const loadSections = async () => {
      const dpaData = await getTermsSections('dpa');
      setSections(dpaData.sections);
      if (dpaData.sections.length > 0) {
        setActiveSection(dpaData.sections[0].id);
      }
    };
    loadSections();
  }, []);

  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    const newRatios = { ...intersectionRatios };
    
    entries.forEach((entry) => {
      newRatios[entry.target.id] = entry.intersectionRatio;
    });
    
    setIntersectionRatios(newRatios);
    
    if (!isManualScroll) {
      const maxRatioSection = Object.entries(newRatios).reduce(
        (max, [id, ratio]) => (ratio > max.ratio ? { id, ratio } : max),
        { id: activeSection, ratio: 0 }
      );

      if (maxRatioSection.ratio > 0) {
        setActiveSection(maxRatioSection.id);
        if (window.location.hash && hasUserScrolled) {
          window.history.replaceState({}, '', window.location.pathname);
          setHasUserScrolled(false);
        }
      }
    }
  }, [intersectionRatios, activeSection, isManualScroll, hasUserScrolled]);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(handleIntersection, {
      rootMargin: '-10% 0px -45% 0px',
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0]
    });

    sections.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observerRef.current?.observe(element);
    });

    const hash = window.location.hash.slice(1);
    if (hash) {
      const element = document.getElementById(hash);
      if (element) {
        setActiveSection(hash);
        scrollToElement(hash);
      }
    }

    const handleHashChange = () => {
      const newHash = window.location.hash.slice(1);
      if (newHash) {
        const element = document.getElementById(newHash);
        if (element) {
          setActiveSection(newHash);
          scrollToElement(newHash);
          setHasUserScrolled(false);
        }
      }
    };

    const handleScroll = () => {
      if (window.location.hash) {
        setHasUserScrolled(true);
        
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        
        scrollTimeoutRef.current = setTimeout(() => {
          if (hasUserScrolled) {
            window.history.replaceState({}, '', window.location.pathname);
            setHasUserScrolled(false);
          }
        }, 150);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      observerRef.current?.disconnect();
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleIntersection, hasUserScrolled, sections]);

  const scrollToSection = useCallback((id: string) => {
    const element = document.getElementById(id);
    if (element) {
      setIsManualScroll(true);
      setActiveSection(id);
      scrollToElement(id, { delay: 100 });
      
      window.history.pushState({}, '', `#${id}`);
      setHasUserScrolled(false);
      
      setTimeout(() => {
        setIsManualScroll(false);
        if (!hasUserScrolled) {
          window.history.replaceState({}, '', window.location.pathname);
        }
      }, 1000);
    }
  }, [hasUserScrolled]);

  return (
    <nav 
      className="hidden lg:block sticky top-24 h-[calc(100vh-6rem)] overflow-y-auto"
      aria-label="Data Processing Agreement navigation"
    >
      <ul className="space-y-2 pr-8" role="list">
        {sections.map(({ id, title }) => (
          <li key={id}>
            <button
              onClick={() => scrollToSection(id)}
              className={`text-left w-full px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
                activeSection === id
                  ? 'bg-primary/10 text-primary-900 font-medium'
                  : 'text-gray-700 hover:text-primary-900 hover:bg-gray-100'
              }`}
              aria-current={activeSection === id ? 'true' : undefined}
              aria-label={`Section: ${title}`}
              type="button"
            >
              {title}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default DPANav; 